import type { LayerKind } from "@/shared/plan";

/**
 * Куда ставится объект каталога. Решает, каким объектом он станет на плане:
 *   • OnlyWall — на грань стены (`Fixture`): розетка, смеситель;
 *   • Everywhere — на пол (`FloorItem` в слое своего раздела): кровать, шкаф;
 *   • Ceiling — на потолок (`FloorItem` в слое электрики): люстра, спот.
 * Потолочный объект ставится и двигается как напольный — точкой на плане, —
 * но движки поднимают его под потолок комнаты, а кабель к нему идёт без спуска.
 */
export enum CatalogAttach {
	OnlyWall = 0,
	Everywhere = 1,
	Ceiling = 2,
}

/** Строковое значение enum, допускающее literal-записи в декларациях каталога. */
export type StringEnumValue<Enum extends string> = Enum | `${Enum}`;

/** Общее у любого элемента каталога, из какого бы раздела он ни был. */
export interface CatalogItemBase {
	id: string;
	subGroup: string;
	name: string;
	description: string;
	/** Цвет элемента: на плане, в панели каталога и как цвет по умолчанию в 3D */
	color: string;
	/**
	 * Тип, к которому относится элемент, — id из `families` раздела. Модели одного типа
	 * (серая, тауп и дубовая двуспальные кровати) в каталоге — одна карточка, а выбирают
	 * между ними у поставленного предмета («Варианты»).
	 */
	family?: string;
}

/** Тип предметов в разделе: одна карточка каталога на несколько моделей. */
export interface CatalogFamily {
	id: string;
	/** Название карточки: «Кровать двуспальная», «Диван» */
	name: string;
	/** Какая модель ставится с карточки; нет — первая в разделе */
	defaultItem?: string;
}

/**
 * Раздел каталога — типизированный список того, что мы даём пользователю: свой тип
 * элемента у каждого раздела (у розетки есть высота установки, у пола — размеры плитки
 * и допустимые укладки). Раздел кормит один слой чертежа (`kind`). Это обычная
 * TS-константа: движки читают её напрямую, никакого стора и загрузки не нужно.
 */
export interface CatalogSection<Item extends CatalogItemBase> {
	kind: LayerKind;
	label: string;
	/** Разрешены ли пользователю личные ширина, глубина и высота экземпляра. */
	canChangeDimensions: boolean;
	subGroups: readonly string[];
	/** Типы, в которые собраны модели раздела (`family` у элемента) */
	families?: readonly CatalogFamily[];
	items: readonly Item[];
}

/**
 * Объёмное представление типа — что построить в 3D: параллелепипед, цилиндр,
 * готовая модель glTF или модель, собранная кодом (`component` — id компонента
 * из раздела каталога в `widgets/catalog/ui/<раздел>/3d`). Размеры в мм: width — вдоль стены (или по X плана),
 * height — по вертикали, depth — от стены (или по Y плана).
 *
 * Модель вписывается ровно в свои размеры, какой бы она ни пришла из редактора:
 * так объём совпадает с символом на плане. `url` — путь к `.glb` в `public/`,
 * `turn` — поворот вокруг вертикали в градусах, если модель смотрит не туда, куда
 * её символ на плане: модели из генераторов смотрят в +Z (к нижнему краю плана),
 * а у дивана, например, перед символа — к верхнему краю, и ему нужен разворот на 180°.
 * `tintable` — модель можно перекрасить: цвет предмета ложится на её текстуру,
 * рисунок ткани остаётся. Модель без текстуры красится `color`, а `roughness`
 * задаёт её блеск: 0 — зеркало, 1 — мел; у керамики около 0,2. Пока модель
 * грузится или не нашлась, на её месте стоит параллелепипед цвета `color`.
 */
export type View3D =
	| { type: "box"; width: number; height: number; depth: number; color: string }
	| { type: "cylinder"; radius: number; height: number; color: string }
	| {
			type: "model";
			url: string;
			width: number;
			height: number;
			depth: number;
			color: string;
			turn?: number;
			tintable?: boolean;
			roughness?: number;
			/** Красить только материал с этим именем (каркас кровати), остальные — в своих цветах */
			tintMaterial?: string;
			/**
			 * Мягкая мебель: обивку можно сменить на ткань или кожу из библиотеки (`FloorItem.fabric`).
			 * Ткань ложится на основной цвет модели — ножки и каркас другого цвета остаются своими.
			 */
			upholstery?: boolean;
			/**
			 * Цвет обивки на атласе модели, если не совпадает с `color`: по нему ищется, куда
			 * ложится ткань. Нужен, когда рама и обивка близкого оттенка (кожаное кресло на дереве).
			 */
			upholsteryColor?: string;
	  }
	| {
			type: "component";
			id: string;
			width: number;
			height: number;
			depth: number;
			color: string;
			tintable?: boolean;
	  };

/** Допустимый личный размер экземпляра по одной оси, мм. */
export interface SizeRange {
	min: number;
	max: number;
}

/** Ось габарита предмета: ширина — по X символа, глубина — по Y, высота — вверх. */
export type SizeAxis = "width" | "depth" | "height";

/** Объект каталога с габаритами и точкой установки: электрика, мебель, сантехника. */
export interface CatalogItem extends CatalogItemBase {
	width: number;
	height: number;
	depth: number;
	elevation: number;
	color: string;
	attach: CatalogAttach;
	/**
	 * Технике нужен свой вывод питания — id элемента электрики (кондиционер — «Вывод под
	 * кондиционер»). Кабель ведут к выводу, а не к самой технике: клик по ней в режиме «Кабель»
	 * ставит вывод рядом и ведёт кабель от него.
	 */
	powerOutlet?: string;
	/** Объём для 3D. Символ для плана каталог не хранит — его рисует компонент раздела */
	view3d: View3D;
	/**
	 * Свои пределы размеров — расширяют общий коридор (`itemSizeRange`), если его не
	 * хватает (ковёр до 5 м); сузить его нельзя, размер предмета выбирает пользователь.
	 */
	sizeLimits?: Partial<Record<SizeAxis, SizeRange>>;
	/**
	 * В плане предмет круглый (стол, пуф): подложка, рамка и зона клика у него — эллипс.
	 * Равенство сторон тут ничего не значит: стиралка 600×600 квадратная.
	 */
	round?: boolean;
}
